import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api";
import {
  apiTrafos,
  CONCLUIDO,
  ETAPAS_INFO,
  dataBR,
  descricaoTrafo,
  duracao,
  entregaAtrasada,
  haQuanto,
  nomeEtapa,
  type Etapa,
  type Movimento,
  type TrafoResumo,
} from "../trafos";
import TrafoFicha from "./TrafoFicha";
import { Trilha } from "./TrafoEtapas";
import ErrorState from "./ErrorState";
import { IconRefresh, IconSearch } from "../icons";

/**
 * CONTROLE DE TRAFOS — o painel da Gestão da Operação.
 *
 * Os trafos são registrados e avançados pela equipe no app do colaborador
 * ("Operacional" → Trafos em Reforma), no banco COMPARTILHADO. Aqui a gestão vê
 * tudo ao vivo: quantos trafos há em cada etapa (e há quanto tempo o mais
 * antigo está parado), a lista com o progresso de cada um e as movimentações
 * conforme acontecem. Clicar abre a mesma ficha do campo, com os poderes da
 * gestão (corrigir concluído, reabrir, excluir — este só administrador).
 *
 * "Ao vivo" = o painel pergunta ao servidor a cada 10 s enquanto a aba está
 * visível (e na hora em que ela volta a ficar visível).
 *
 * HISTÓRICO: o cartão "Concluídos" abre TODOS os trafos já concluídos (antes
 * eram só os dos últimos 30 dias, e o resto sumia da tela). Cada um abre a
 * ficha, de onde sai o relatório de rastreabilidade (Imprimir / PDF).
 */

const ATUALIZA_MS = 10_000;
/** O cartão de concluídos mostra quantos saíram neste período (a lista traz todos). */
const DIAS_CONCLUIDOS = 30;

type Filtro = Etapa | "andamento" | "concluidos";

export default function ControleTrafosView({ somenteLeitura = false }: { somenteLeitura?: boolean }) {
  const [trafos, setTrafos] = useState<TrafoResumo[]>([]);
  const [movs, setMovs] = useState<Movimento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [semConexao, setSemConexao] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("andamento");
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState<number | null>(null);
  /** Movimentações que chegaram nesta última atualização (destaque breve). */
  const [novos, setNovos] = useState<Set<number>>(() => new Set());
  const ultimoMov = useRef<number>(0);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const [lista, m] = await Promise.all([apiTrafos.listar("todos"), apiTrafos.movimentos(40)]);
      setTrafos(lista);
      setMovs(m);
      // Destaca o que é novo desde a última volta (não na primeira carga).
      const maior = m.reduce((acc, x) => Math.max(acc, x.id), 0);
      if (ultimoMov.current > 0) setNovos(new Set(m.filter((x) => x.id > ultimoMov.current).map((x) => x.id)));
      ultimoMov.current = Math.max(ultimoMov.current, maior);
      setAtualizadoEm(new Date());
      setSemConexao(false);
      setErro(null);
    } catch (err) {
      if (silencioso) setSemConexao(true);
      else setErro(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") carregar(true);
    }, ATUALIZA_MS);
    const aoVoltar = () => {
      if (document.visibilityState === "visible") carregar(true);
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [carregar]);

  const agora = atualizadoEm?.getTime() ?? Date.now();

  /** Todos os concluídos (o histórico) e quantos saíram nos últimos 30 dias. */
  const limiteConcluidos = agora - DIAS_CONCLUIDOS * 86_400_000;
  const emAndamento = useMemo(() => trafos.filter((t) => t.etapa !== CONCLUIDO), [trafos]);
  const concluidos = useMemo(() => trafos.filter((t) => t.etapa === CONCLUIDO), [trafos]);
  const concluidosRecentes = useMemo(
    () => concluidos.filter((t) => t.data_conclusao && new Date(t.data_conclusao).getTime() >= limiteConcluidos).length,
    [concluidos, limiteConcluidos],
  );

  /** Por etapa: quantos e há quanto tempo está o mais antigo. */
  const porEtapa = useMemo(
    () =>
      ETAPAS_INFO.map((p) => {
        const daEtapa = emAndamento.filter((t) => t.etapa === p.key);
        const maisAntigo = daEtapa.reduce<string | null>(
          (acc, t) => (!acc || t.etapa_desde < acc ? t.etapa_desde : acc),
          null,
        );
        return { ...p, total: daEtapa.length, maisAntigo };
      }),
    [emAndamento],
  );
  const atrasados = emAndamento.filter((t) => entregaAtrasada(t)).length;

  const visiveis = useMemo(() => {
    const q = busca
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim()
      .toLowerCase();
    // Buscando, procura em TODOS (inclusive concluídos antigos).
    const base = q
      ? trafos
      : filtro === "andamento"
        ? emAndamento
        : filtro === "concluidos"
          ? concluidos
          : emAndamento.filter((t) => t.etapa === filtro);
    if (!q) return base;
    return base.filter((t) =>
      [t.id, t.cliente, t.os_numero, t.nota_fiscal, t.identificacao, t.fabricante, t.numero_serie, t.potencia_kva]
        .map((v) => String(v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase())
        .some((v) => v.includes(q)),
    );
  }, [trafos, emAndamento, concluidos, filtro, busca]);

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={() => carregar()} />;

  const escolher = (f: Filtro) => setFiltro((atual) => (atual === f ? "andamento" : f));
  const tituloLista = busca
    ? `Busca: ${visiveis.length} trafo(s)`
    : filtro === "andamento"
      ? `Em reforma (${emAndamento.length})`
      : filtro === "concluidos"
        ? `Histórico — trafos concluídos (${concluidos.length})`
        : `${nomeEtapa(filtro)} (${visiveis.length})`;

  return (
    <div className="trafos ctrafos">
      <div className="crud-toolbar trafos-barra">
        <div className={`ctrafos-vivo${semConexao ? " off" : ""}`} aria-live="polite">
          <span className="ctrafos-vivo-ponto" aria-hidden="true" />
          {semConexao
            ? "Sem conexão — tentando de novo"
            : atualizadoEm
              ? `Ao vivo · atualizado às ${atualizadoEm.toLocaleTimeString("pt-BR")}`
              : "Conectando…"}
          {atrasados > 0 && (
            <span className="trafo-atrasado">
              · {atrasados} com entrega vencida
            </span>
          )}
        </div>
        <div className="toolbar-right trafos-barra-direita">
          <div className="search-wrap">
            <IconSearch size={16} />
            <input
              className="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Cliente, OS, série, nº do trafo…"
            />
          </div>
          <button type="button" className="icon-btn" onClick={() => carregar()} title="Atualizar agora" aria-label="Atualizar">
            <IconRefresh size={18} />
          </button>
        </div>
      </div>

      {/* As 5 etapas do fluxo + concluídos. Clicar filtra a lista. */}
      <div className="ctrafos-etapas">
        {porEtapa.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`ctrafos-etapa${filtro === p.key ? " on" : ""}`}
            onClick={() => escolher(p.key)}
            aria-pressed={filtro === p.key}
          >
            <span className="ctrafos-etapa-num">{p.numero}</span>
            <span className="ctrafos-etapa-total">{p.total}</span>
            <span className="ctrafos-etapa-nome">{p.curto}</span>
            <span className="ctrafos-etapa-tempo">
              {p.maisAntigo ? `mais antigo há ${duracao(p.maisAntigo, agora)}` : "—"}
            </span>
          </button>
        ))}
        <button
          type="button"
          className={`ctrafos-etapa concluidos${filtro === "concluidos" ? " on" : ""}`}
          onClick={() => escolher("concluidos")}
          aria-pressed={filtro === "concluidos"}
          title="Histórico: todos os trafos concluídos, com o relatório de rastreabilidade de cada um"
        >
          <span className="ctrafos-etapa-num">✓</span>
          <span className="ctrafos-etapa-total">{concluidos.length}</span>
          <span className="ctrafos-etapa-nome">Concluídos</span>
          <span className="ctrafos-etapa-tempo">
            {concluidosRecentes} nos últimos {DIAS_CONCLUIDOS} dias
          </span>
        </button>
      </div>

      <div className="ctrafos-colunas">
        <section className="ctrafos-lista">
          <div className="ctrafos-lista-cab">
            <h3 className="section-title">{tituloLista}</h3>
            {(filtro !== "andamento" || busca) && (
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setFiltro("andamento");
                  setBusca("");
                }}
              >
                Ver todos em reforma
              </button>
            )}
          </div>
          {filtro === "concluidos" && !busca && concluidos.length > 0 && (
            <p className="muted ctrafos-dica">
              Clique no trafo e use <strong>Imprimir / PDF</strong> para o relatório de rastreabilidade (etapas,
              histórico completo e fotos).
            </p>
          )}
          <div className="table-wrap">
            {carregando && trafos.length === 0 ? (
              <div className="empty">Carregando…</div>
            ) : visiveis.length === 0 ? (
              <div className="empty">
                {busca ? "Nenhum trafo encontrado." : "Nenhum trafo aqui agora."}
              </div>
            ) : (
              <table className="table ctrafos-tabela">
                <thead>
                  <tr>
                    <th>Nº</th>
                    <th>Trafo</th>
                    <th>Etapa</th>
                    <th>Serviços</th>
                    <th>Última movimentação</th>
                    <th>Entrega</th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map((t) => (
                    <tr key={t.id} className="ctrafos-linha" onClick={() => setAberto(t.id)} tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setAberto(t.id);
                      }}
                    >
                      <td className="ctrafos-num">{t.id}</td>
                      <td>
                        <strong className="ctrafos-cliente">
                          <span className="ctrafos-celular">Nº {t.id} · </span>
                          {t.cliente}
                        </strong>
                        <span className="ctrafos-sub">
                          {descricaoTrafo(t)}
                          {t.os_numero ? ` · OS ${t.os_numero}` : ""}
                        </span>
                      </td>
                      <td>
                        <Trilha etapa={t.etapa} />
                        <span className="ctrafos-sub">
                          {t.etapa === CONCLUIDO
                            ? `Concluído ${haQuanto(t.data_conclusao, agora)}`
                            : `${nomeEtapa(t.etapa)} · há ${duracao(t.etapa_desde, agora)}`}
                        </span>
                      </td>
                      <td>
                        <span className="ctrafos-celular">Serviços </span>
                        {t.servicos_total > 0 ? (
                          <>
                            {t.servicos_feitos}/{t.servicos_total}
                            {t.servicos_andamento > 0 && (
                              <span className="ctrafos-sub">{t.servicos_andamento} em andamento</span>
                            )}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="ctrafos-ultimo">
                        {t.ultimo_texto ? (
                          <>
                            <span className="ctrafos-ultimo-texto">{t.ultimo_texto}</span>
                            <span className="ctrafos-sub">
                              {t.ultimo_usuario ?? "—"} · {haQuanto(t.ultimo_em, agora)}
                            </span>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className="ctrafos-celular">Entrega </span>
                        {t.previsao_entrega ? (
                          <span className={entregaAtrasada(t) ? "trafo-atrasado" : undefined}>
                            {dataBR(t.previsao_entrega)}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <aside className="ctrafos-movs">
          <h3 className="section-title">Movimentações recentes</h3>
          {movs.length === 0 ? (
            <p className="muted">Nada registrado ainda. As movimentações da equipe aparecem aqui conforme acontecem.</p>
          ) : (
            <ol className="ctrafos-feed">
              {movs.map((m) => (
                <li key={m.id} className={novos.has(m.id) ? "is-novo" : undefined}>
                  <button type="button" onClick={() => setAberto(m.trafo_id)}>
                    <span className="ctrafos-feed-quando">{haQuanto(m.created_at, agora)}</span>
                    <span className="ctrafos-feed-trafo">
                      Nº {m.trafo_id} · {m.cliente}
                    </span>
                    <span className="ctrafos-feed-texto">{m.texto}</span>
                    <span className="ctrafos-sub">{m.usuario ?? "—"}</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </aside>
      </div>

      {aberto !== null && (
        <TrafoFicha
          id={aberto}
          gestao
          somenteLeitura={somenteLeitura}
          onClose={() => setAberto(null)}
          onChanged={() => carregar(true)}
        />
      )}
    </div>
  );
}
